import { AuthState } from '@/types/authState'
import type {
  TaxTransaction,
  TaxTransactionSearchResponse,
  TransactionSearchFilter,
} from '@/types/taxTransaction'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import { ApiError } from '@/util/ApiError'
import { normalizeError } from '@/util/errorNormalizer'
import { createTracedHeaders } from '@/util/datadogRum'

export const searchTaxTransactions = async (
  authState: AuthState,
  merchantId: string,
  filter: TransactionSearchFilter,
  page: number,
  size: number,
  sort: string,
): Promise<TaxTransactionSearchResponse> => {
  // The gateway URL may be relative (same-origin), so resolve it against the page.
  const url = new URL(
    API_ENDPOINTS.TAX_TRANSACTION.SEARCH_BY_MERCHANT(merchantId),
    window.location.origin,
  )

  url.searchParams.set('page', page.toString())
  url.searchParams.set('size', size.toString())
  if (sort) {
    url.searchParams.set('sort', sort)
  }

  // Map individual filter parameters
  if (filter.startDate) {
    url.searchParams.set(
      'startDate',
      filter.startDate.toISOString().split('T')[0],
    )
  }
  if (filter.endDate) {
    url.searchParams.set('endDate', filter.endDate.toISOString().split('T')[0])
  }
  if (filter.externalId) {
    url.searchParams.set('externalId', filter.externalId)
  }
  if (filter.referenceId) {
    url.searchParams.set('referenceId', filter.referenceId)
  }
  if (filter.providerId) {
    url.searchParams.set('providerId', filter.providerId)
  }
  if (filter.providerReferenceId) {
    url.searchParams.set('providerReferenceId', filter.providerReferenceId)
  }
  if (filter.engineTransactionId) {
    url.searchParams.set('engineTransactionId', filter.engineTransactionId)
  }
  if (filter.engineRefundId) {
    url.searchParams.set('engineRefundId', filter.engineRefundId)
  }
  if (filter.chargebackId) {
    url.searchParams.set('chargebackId', filter.chargebackId)
  }

  // Create traced headers with DataDog trace ID
  const { traceId, headers } = createTracedHeaders({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authState.user?.accessToken}`,
  })

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
  })

  if (!response.ok) {
    // Read response body and create ApiError with full context
    let responseData: unknown = null
    let responseText = ''
    try {
      responseText = await response.text()
      if (responseText) {
        responseData = JSON.parse(responseText)
      }
    } catch {
      // Response body couldn't be parsed as JSON - use plain text as message
      if (responseText) {
        responseData = { message: responseText }
      }
    }

    // Use normalizeError to handle various API error formats
    const apiError = normalizeError(responseData ?? { message: `HTTP ${response.status} error` }, {
      traceId,
      url: url.toString(),
      method: 'GET',
    })

    // Ensure status code is set
    if (!apiError.statusCode) {
      throw new ApiError({
        message: apiError.message,
        code: apiError.code,
        statusCode: response.status,
        fieldErrors: apiError.fieldErrors,
        originalResponse: responseData,
        traceId,
        url: url.toString(),
        method: 'GET',
      })
    }

    throw apiError
  }

  return (await response.json()) as TaxTransactionSearchResponse
}

export const getTransactionDetails = async (
  authState: AuthState,
  merchantId: string,
  transactionId: string,
): Promise<TaxTransaction> => {
  const requestUrl = API_ENDPOINTS.TAX_TRANSACTION.DETAILS(merchantId, transactionId)

  // Create traced headers with DataDog trace ID
  const { traceId, headers } = createTracedHeaders({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authState.user?.accessToken}`,
  })

  const response = await fetch(requestUrl, {
    method: 'GET',
    headers,
  })

  if (!response.ok) {
    // Read response body and create ApiError with full context
    let responseData: unknown = null
    let responseText = ''
    try {
      responseText = await response.text()
      if (responseText) {
        responseData = JSON.parse(responseText)
      }
    } catch {
      // Response body couldn't be parsed as JSON - use plain text as message
      if (responseText) {
        responseData = { message: responseText }
      }
    }

    // Use normalizeError to handle various API error formats
    const apiError = normalizeError(responseData ?? { message: `HTTP ${response.status} error` }, {
      traceId,
      url: requestUrl,
      method: 'GET',
    })

    // Ensure status code is set
    if (!apiError.statusCode) {
      throw new ApiError({
        message: apiError.message,
        code: apiError.code,
        statusCode: response.status,
        fieldErrors: apiError.fieldErrors,
        originalResponse: responseData,
        traceId,
        url: requestUrl,
        method: 'GET',
      })
    }

    throw apiError
  }

  return await response.json()
}
